import React, { useState, useEffect, useMemo } from 'react';
import { Container, Card, Form, Button, Alert, Row, Col, Tabs, Tab, Modal } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { collection, addDoc, query, where, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';

const PendaftaranTA = () => {
  const { currentUser, userRole } = useAuth();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [existingTA, setExistingTA] = useState(null);
  const [checkingExisting, setCheckingExisting] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [linkError, setLinkError] = useState('');
  const [dosenList, setDosenList] = useState([]);
  const [loadingDosen, setLoadingDosen] = useState(true);
  const [showChangeSupervisorModal, setShowChangeSupervisorModal] = useState(false);
  const [selectedNewPembimbing, setSelectedNewPembimbing] = useState([]);
  const navigate = useNavigate();

  // Cek apakah mahasiswa sudah mendaftar tugas akhir - dipindahkan keluar useEffect agar bisa dipanggil dari handler
  const checkExistingTA = async () => {
      try {
        if (!currentUser) return;

        const taQuery = query(
          collection(db, "tugasAkhir"),
          where("mahasiswaId", "==", currentUser.uid)
        );
        const taSnapshot = await getDocs(taQuery);
        
        if (!taSnapshot.empty) {
          const taData = taSnapshot.docs[0].data();
          taData.id = taSnapshot.docs[0].id;

          // Jika status approved dan pembimbing berupa array of string (ID), fetch data dosen
          if (taData.status === 'approved') {
            // Fallback: jika pembimbing belum ada, gunakan pembimbingId
            if (!taData.pembimbing && taData.pembimbingId) {
              taData.pembimbing = taData.pembimbingId;
            }
            // Patch: jika pembimbing satu string, ubah jadi array satu elemen
            if (typeof taData.pembimbing === 'string') {
              taData.pembimbing = [taData.pembimbing];
            }
            if (taData.status === 'approved' && (!Array.isArray(taData.pembimbing) || taData.pembimbing.length === 0)) {
              console.warn('Field pembimbing tidak ada, bukan array, atau kosong:', taData.pembimbing);
            } else if (typeof taData.pembimbing[0] === 'string') {
            const pembimbingObjs = [];
            for (const pembimbingId of taData.pembimbing) {
              let dosenDoc = await getDocs(query(collection(db, "users"), where("uid", "==", pembimbingId)));
              if (!dosenDoc.empty) {
                const dosenData = dosenDoc.docs[0].data();
                pembimbingObjs.push({ id: pembimbingId, nama: dosenData.nama, nip: dosenData.nip });
                console.log(`Pembimbing ditemukan dengan uid: ${pembimbingId}`);
              } else {
                // Coba dengan field id
                dosenDoc = await getDocs(query(collection(db, "users"), where("id", "==", pembimbingId)));
                if (!dosenDoc.empty) {
                  const dosenData = dosenDoc.docs[0].data();
                  pembimbingObjs.push({ id: pembimbingId, nama: dosenData.nama, nip: dosenData.nip });
                  console.log(`Pembimbing ditemukan dengan id: ${pembimbingId}`);
                } else {
                  // PATCH: fallback cari berdasarkan document ID
                  const dosenDocById = await getDoc(doc(db, "users", pembimbingId));
                  if (dosenDocById.exists()) {
                    const dosenData = dosenDocById.data();
                    pembimbingObjs.push({ id: pembimbingId, nama: dosenData.nama, nip: dosenData.nip });
                    console.log(`Pembimbing ditemukan dengan documentId: ${pembimbingId}`);
                  } else {
                    console.warn(`Pembimbing dengan UID/ID/DocID ${pembimbingId} tidak ditemukan di koleksi users`);
                  }
                }
              }
            }
            console.log('PEMBIMBING FETCHED:', pembimbingObjs);
            taData.pembimbing = pembimbingObjs;
            }
          }

          // Fetch kandidat pembimbing data if exists
          if (taData.kandidatPembimbingId && Array.isArray(taData.kandidatPembimbingId)) {
            const kandidatPembimbingObjs = [];
            for (const pembimbingId of taData.kandidatPembimbingId) {
              const dosenDocById = await getDoc(doc(db, "users", pembimbingId));
              if (dosenDocById.exists()) {
                const dosenData = dosenDocById.data();
                kandidatPembimbingObjs.push({ id: pembimbingId, nama: dosenData.nama, nip: dosenData.nip });
              }
            }
            taData.kandidatPembimbing = kandidatPembimbingObjs;
          }

          setExistingTA(taData);
        }
      } catch (error) {
        console.error("Error checking existing TA:", error);
        setError("Terjadi kesalahan saat memeriksa data tugas akhir");
      } finally {
        setCheckingExisting(false);
      }
  };

  useEffect(() => {
    // Fetch available lecturers
    const fetchDosen = async () => {
      try {
        setLoadingDosen(true);
        const dosenQuery = query(
          collection(db, "users"),
          where("role", "==", "dosen"),
          where("verified", "==", true)
        );
        const dosenSnapshot = await getDocs(dosenQuery);
        
        const dosenData = [];
        dosenSnapshot.forEach(doc => {
          dosenData.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        setDosenList(dosenData);
      } catch (error) {
        console.error("Error fetching dosen:", error);
        setError("Terjadi kesalahan saat memuat data dosen pembimbing");
      } finally {
        setLoadingDosen(false);
      }
    };

    fetchDosen();
    checkExistingTA();
  }, [currentUser]);

  // Memoize initialValues dan validationSchema
  const getInitialValues = () => {
    return existingTA 
      ? { 
          judul: existingTA.judul || '', 
          driveLink: existingTA.driveLink || '',
          kandidatPembimbing: existingTA.kandidatPembimbingId || []
        }
      : { 
          judul: '', 
          driveLink: '',
          kandidatPembimbing: []
        };
  };

  const validationSchema = useMemo(() => Yup.object({
    judul: Yup.string().required('Judul tugas akhir wajib diisi'),
    driveLink: Yup.string()
      .required('Link Google Drive wajib diisi')
      .test('is-drive-link', 'Link harus dari Google Drive (drive.google.com)', 
        value => value && value.includes('drive.google.com')),
    kandidatPembimbing: Yup.array()
      .max(2, 'Maksimal 2 dosen pembimbing yang dapat dipilih')
      .test(
        'required-length',
        'Pilih minimal 1 dosen pembimbing',
        value => value && value.length > 0
      )
  }), []);
  
  // Fungsi untuk validasi link Google Drive
  const validateDriveLink = (link) => {
    if (!link) {
      setLinkError('Link Google Drive wajib diisi');
      return false;
    }
    // Lebih fleksibel dalam validasi link Google Drive
    if (!link.includes('drive.google.com')) {
      setLinkError('Link harus dari Google Drive');
      return false;
    }
    setLinkError('');
    return true;
  };
  
  // Fungsi untuk memformat nama file proposal sesuai ketentuan
  const formatProposalFilename = (nim, nama) => {
    // Format: prop_nim_nama mahasiswa
    return `prop_${nim}_${nama.replace(/\s+/g, '_')}`;
  };
  
  // Fungsi untuk memformat nama file revisi sesuai ketentuan
  const formatRevisionFilename = (nim, nama, revisionNumber) => {
    // Format: rev{N}_prop_nim_nama mahasiswa
    return `rev${revisionNumber}_prop_${nim}_${nama.replace(/\s+/g, '_')}`;
  };

  const handleSubmit = async (values, formikBag) => {
    console.log('SUBMIT: mulai', values);
    
    // Extract setSubmitting and setErrors if available
    const setSubmitting = formikBag?.setSubmitting;
    const setErrors = formikBag?.setErrors;
    
    setLoading(true);
    setError('');
    setLinkError('');
    
    try {
      // Skip extra validation - trust Formik's validation
      if (existingTA) {
        console.log('SUBMIT: updateDoc');
        // Simpan usulan perubahan, bisa diajukan berulang kali
        // Cek apakah ini adalah P1 atau perlu approval P1 dulu
        const taDocRef = doc(db, "tugasAkhir", existingTA.id);
        const taDocSnapshot = await getDoc(taDocRef);
        const taData = taDocSnapshot.exists() ? taDocSnapshot.data() : null;
        
        // Tentukan status berdasarkan apakah ada P1 yang sudah di-assign
        let statusUsulan = 'pending_p1_approval'; // default: butuh P1 approval dulu
        let pembimbing1Id = null;
        
        if (taData && taData.pembimbingId && Array.isArray(taData.pembimbingId) && taData.pembimbingId.length > 0) {
          pembimbing1Id = taData.pembimbingId[0];
          statusUsulan = 'pending_p1_approval';
        } else {
          // Jika belum ada P1, langsung ke admin
          statusUsulan = 'pending_approval';
        }
        
        await updateDoc(taDocRef, {
          usulanPerubahan: {
            judul: values.judul,
            driveLink: values.driveLink,
            kandidatPembimbingId: values.kandidatPembimbing,
            status: statusUsulan,
            tanggal_usulan: new Date().toISOString(),
            pembimbing1Id: pembimbing1Id,
            previousJudul: existingTA.judul, // Simpan judul lama untuk tracking
          }
        });
      } else {
        // Create new document
        await addDoc(collection(db, "tugasAkhir"), {
          judul: values.judul,
          mahasiswaId: currentUser.uid,
          driveLink: values.driveLink,
          kandidatPembimbingId: values.kandidatPembimbing,
          status: 'pending',
          tanggal_daftar: new Date().toISOString(),
        });
      }

      setSuccess(true);
      console.log('SUBMIT: sukses');
      setTimeout(() => {
        console.log('SUBMIT: redirect');
        navigate('/dashboard');
      }, 2000);

    } catch (err) {
      console.error('SUBMIT ERROR:', err);
      setError('Gagal menyimpan data: ' + err.message);
      if (setSubmitting) setSubmitting(false);
    } finally {
      setLoading(false);
      if (setSubmitting) setSubmitting(false);
    }
  };

  if (checkingExisting) {
    return (
      <Container className="py-4">
        <Alert variant="info">Memeriksa data tugas akhir...</Alert>
      </Container>
    );
  }

  if (existingTA && !isEditing) {
    const isRejected = existingTA.status === 'rejected';
    const usulan = existingTA.usulanPerubahan;
    return (
      <Container className="py-4">
        <Card>
          <Card.Body>
            <Card.Title>Pendaftaran Tugas Akhir</Card.Title>
            {/* Notifikasi usulan perubahan */}
            {usulan && usulan.status === 'pending' && (
              <Alert variant="info">
                <strong>Perubahan data tugas akhir sedang menunggu verifikasi.</strong><br/>
                Judul usulan: <strong>{usulan.judul}</strong><br/>
                Link usulan: <a href={usulan.driveLink} target="_blank" rel="noopener noreferrer">{usulan.driveLink}</a>
                <div className="mt-2 text-muted">
                  {usulan.status === 'pending_p1_approval' ? 
                    "Menunggu persetujuan Pembimbing 1. Setelah disetujui, judul akan langsung diupdate." : 
                    "Menunggu persetujuan."}
                </div>
              </Alert>
            )}
            {usulan && usulan.status === 'rejected' && (
              <Alert variant="danger">
                <strong>Usulan perubahan data TA ditolak.</strong>
                {usulan.alasan && (<div className="mt-1">Alasan: {usulan.alasan}</div>)}
                <Button variant="warning" className="mt-2" onClick={() => setIsEditing(true)}>Ajukan Perubahan Data Baru</Button>
              </Alert>
            )}
            {usulan && usulan.status === 'approved' && (
              <Alert variant="success">
                <strong>Usulan perubahan data TA telah disetujui.</strong><br/>
                Judul baru: <strong>{usulan.judul}</strong>
                <Button variant="primary" className="mt-2 ms-2" onClick={() => setIsEditing(true)}>Ajukan Perubahan Data Lain</Button>
              </Alert>
            )}
            {/* Notifikasi usulan perubahan pembimbing */}
            {existingTA.usulanPerubahanPembimbing && existingTA.usulanPerubahanPembimbing.status === 'pending' && (
              <Alert variant="info">
                <strong>Usulan perubahan pembimbing sedang menunggu persetujuan admin.</strong><br/>
                Tanggal pengajuan: {new Date(existingTA.usulanPerubahanPembimbing.tanggal_usulan).toLocaleDateString()}
              </Alert>
            )}
            {existingTA.usulanPerubahanPembimbing && existingTA.usulanPerubahanPembimbing.status === 'rejected' && (
              <Alert variant="danger">
                <strong>Usulan perubahan pembimbing ditolak.</strong>
                {existingTA.usulanPerubahanPembimbing.alasan && (
                  <div className="mt-1">Alasan: {existingTA.usulanPerubahanPembimbing.alasan}</div>
                )}
              </Alert>
            )}
            <Alert variant={isRejected ? "danger" : "info"}>
              <div className="mb-2">
                Anda sudah mendaftar tugas akhir dengan judul: <strong>{existingTA.judul}</strong>
              </div>
              <div className="mb-2">
                Status: <strong>{
                  existingTA.status === 'pending' ? 'Menunggu Persetujuan' :
                  existingTA.status === 'approved' ? 'Disetujui' :
                  existingTA.status === 'rejected' ? 'Ditolak' :
                  existingTA.status === 'completed' ? 'Selesai' : existingTA.status
                }</strong>
              </div>
              
              {/* Tampilkan kandidat dosen pembimbing jika status pending */}
              {existingTA.status === 'pending' && existingTA.kandidatPembimbing && existingTA.kandidatPembimbing.length > 0 ? (
                <div className="mb-2">
                  <strong>Kandidat Dosen Pembimbing:</strong>
                  <ul className="mb-2">
                    {existingTA.kandidatPembimbing.map((p, idx) => (
                      <li key={p.id}>
                        {p.nama} (NIP: {p.nip || '-'})
                      </li>
                    ))}
                  </ul>
                  <small className="text-muted">Admin akan menentukan pembimbing yang sesuai dari kandidat yang Anda pilih.</small>
                </div>
              ) : existingTA.status === 'pending' && existingTA.kandidatPembimbingId && existingTA.kandidatPembimbingId.length > 0 ? (
                <div className="mb-2">
                  <strong>Kandidat Dosen Pembimbing Telah Dipilih</strong>
                  <div className="text-muted">
                    Admin akan menentukan pembimbing yang sesuai dari kandidat yang Anda pilih.
                  </div>
                </div>
              ) : null}
              
              {/* Tampilkan dosen pembimbing jika sudah approved */}
              {existingTA.status === 'approved' && Array.isArray(existingTA.pembimbing) && existingTA.pembimbing.length > 0 ? (
                <div className="mb-2">
                  <strong>Dosen Pembimbing yang Disetujui/Menyetujui:</strong>
                  <ul className="mb-2">
                    {existingTA.pembimbing.map((p, idx) => (
                      <li key={p.id}>
                        {p.nama} (NIP: {p.nip || '-'})
                      </li>
                    ))}
                  </ul>
                </div>
              ) : existingTA.status === 'approved' && (
                <div className="mb-2 text-danger">
                  Data dosen pembimbing belum tersedia. Silakan hubungi admin.
                </div>
              )}
              {/* Tampilkan dosen penguji jika sudah approved */}
              {existingTA.status === 'approved' && Array.isArray(existingTA.penguji) && existingTA.penguji.length > 0 ? (
                <div className="mb-2">
                  <strong>Dosen Penguji:</strong>
                  <ul className="mb-2">
                    {existingTA.penguji.map((p, idx) => (
                      <li key={p.id}>
                        {p.nama} (NIP: {p.nip || '-'})
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              
              {isRejected && existingTA.komentar_penolakan && (
                <div className="mt-3">
                  <h6>Komentar Penolakan:</h6>
                  <p className="mb-0">{existingTA.komentar_penolakan}</p>
                </div>
              )}
            </Alert>
            
            <div className="d-flex gap-2">
              {/* Tombol ajukan perubahan data - bisa diajukan berulang kali */}
              {existingTA.status === 'approved' && (
                <>
                  <Button 
                    variant="warning" 
                    onClick={() => setIsEditing(true)}
                  >
                    {usulan && usulan.status === 'pending' ? 'Ajukan Perubahan Data Baru' : 'Ajukan Perubahan Judul'}
                  </Button>
                  <Button 
                    variant="info" 
                    onClick={() => setShowChangeSupervisorModal(true)}
                  >
                    Ajukan Perubahan Pembimbing
                  </Button>
                </>
              )}
              {isRejected && (
                <Button 
                  variant="warning" 
                  onClick={() => setIsEditing(true)}
                >
                  Edit dan Ajukan Ulang
                </Button>
              )}
              <Button 
                variant="primary" 
                onClick={() => navigate('/mahasiswa')}
              >
                Kembali ke Dashboard
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  if (isEditing) {
    return (
      <Container className="py-4">
        <Card>
          <Card.Body>
            <Card.Title>Edit Tugas Akhir</Card.Title>
            {error && <Alert variant="danger">{error}</Alert>}
            {success && (
              <Alert variant="success">
                Pendaftaran tugas akhir berhasil! Silakan tunggu persetujuan dari Penanggung Jawab Tugas Akhir.
                Anda akan dialihkan ke dashboard dalam beberapa detik.
              </Alert>
            )}
            
            <Alert variant="info">
              <strong>Petunjuk Upload Proposal:</strong>
              <ol>
                <li>Pastikan folder Google Drive Anda masih dapat diakses dan diedit oleh semua orang dengan link</li>
                <li>Upload file proposal baru Anda ke folder tersebut dengan format nama: <code>prop_NIM_Nama_Anda</code></li>
                <li>Jika ada komentar dari dosen, perhatikan komentar tersebut untuk perbaikan proposal</li>
              </ol>
            </Alert>
            
            <Formik
              initialValues={getInitialValues()}
              validationSchema={validationSchema}
              onSubmit={(values, { setSubmitting, setErrors }) => {
                console.log('onSubmit called with values:', values);
                handleSubmit(values, { setSubmitting, setErrors });
              }}
            >
{({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue }) => {                console.log('Form errors:', errors);
                return (
                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>Judul Proposal</Form.Label>
                      <Form.Control
  type="text"
  name="judul"
  value={values.judul}
  onChange={handleChange}
  onBlur={handleBlur}
                        isInvalid={touched.judul && errors.judul}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.judul}
                      </Form.Control.Feedback>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Link Google Drive Folder</Form.Label>
                      <Form.Control
                        type="text"
                        name="driveLink"
                        value={values.driveLink}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.driveLink && errors.driveLink}
                        placeholder="https://drive.google.com/drive/folders/...?usp=sharing"
                      />
                      <Form.Text className="text-muted">
                        Masukkan link folder Google Drive yang telah dibagikan dengan akses "Semua orang dengan link dapat mengedit".
                        Pastikan Anda memasukkan link folder, bukan link file.
                      </Form.Text>
                      <Form.Control.Feedback type="invalid">
                        {errors.driveLink}
                      </Form.Control.Feedback>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Pilih Kandidat Dosen Pembimbing (maksimal 2)</Form.Label>
                      {loadingDosen ? (
                        <p>Memuat daftar dosen...</p>
                      ) : (
                        <div className="border rounded p-3">
                          {dosenList.length > 0 ? (
                            dosenList.map(dosen => (
                              <Form.Check
                                key={dosen.id}
                                type="checkbox"
                                id={`kandidat-${dosen.id}`}
                                label={`${dosen.nama} ${dosen.nip ? `(${dosen.nip})` : ''}`}
                                checked={values.kandidatPembimbing.includes(dosen.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    if (values.kandidatPembimbing.length < 2) {
                                      setFieldValue('kandidatPembimbing', [...values.kandidatPembimbing, dosen.id]);
                                    }
                                  } else {
                                    setFieldValue('kandidatPembimbing', values.kandidatPembimbing.filter(id => id !== dosen.id));
                                  }
                                }}
                                disabled={values.kandidatPembimbing.length >= 2 && !values.kandidatPembimbing.includes(dosen.id)}
                                className="mb-2"
                              />
                            ))
                          ) : (
                            <p className="text-muted">Tidak ada dosen tersedia</p>
                          )}
                        </div>
                      )}
                      <Form.Text className="text-muted">
                        Pilih maksimal 2 dosen sebagai kandidat pembimbing. Admin akan menentukan siapa yang menjadi pembimbing Anda.
                      </Form.Text>
                      {touched.kandidatPembimbing && errors.kandidatPembimbing && (
                        <div className="text-danger mt-1">{errors.kandidatPembimbing}</div>
                      )}
                    </Form.Group>
                    
                    <Alert variant="warning">
                      <strong>Perhatian:</strong> Dengan mengajukan ulang, Anda akan mengganti proposal yang sudah ada dengan proposal baru.
                    </Alert>
                    
                    <Button 
                      variant="primary" 
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Memproses...' : 'Simpan'}
                    </Button>
                  </Form>
                );
              }}
            </Formik>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Card>
        <Card.Body>
          <Card.Title>Pendaftaran Tugas Akhir</Card.Title>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && (
            <Alert variant="success">
              Pendaftaran tugas akhir berhasil! Silakan tunggu persetujuan dari Penanggung Jawab Tugas Akhir.
              Anda akan dialihkan ke dashboard dalam beberapa detik.
            </Alert>
          )}
          
          <Alert variant="info">
            <strong>Petunjuk Upload Proposal:</strong>
            <ol>
              <li>Buat folder di Google Drive dan atur akses folder menjadi "Semua orang dengan link dapat mengedit"</li>
              <li>Salin link folder (bukan link file) dan masukkan pada form di bawah</li>
              <li>Upload file proposal Anda ke folder tersebut dengan format nama: <code>prop_NIM_Nama_Anda</code> (contoh: <code>prop_12345_John_Doe</code>)</li>
              <li>Jika proposal Anda ditolak dan perlu direvisi, dosen akan mengupload file revisi dengan format: <code>rev1_prop_NIM_Nama_Anda</code></li>
            </ol>
          </Alert>
          
          <Formik
            initialValues={getInitialValues()}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue }) => {
              console.log('Form errors:', errors);
              return (
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Judul Proposal</Form.Label>
                    <Form.Control
                      type="text"
                      name="judul"
                      value={values.judul}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.judul && errors.judul}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.judul}
                    </Form.Control.Feedback>
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Link Google Drive Folder</Form.Label>
                    <Form.Control
                      type="text"
                      name="driveLink"
                      value={values.driveLink}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.driveLink && errors.driveLink}
                      placeholder="https://drive.google.com/drive/folders/...?usp=sharing"
                    />
                    <Form.Text className="text-muted">
                      Masukkan link folder Google Drive yang telah dibagikan dengan akses "Semua orang dengan link dapat mengedit".
                      Pastikan Anda memasukkan link folder, bukan link file.
                    </Form.Text>
                    <Form.Control.Feedback type="invalid">
                      {errors.driveLink}
                    </Form.Control.Feedback>
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Pilih Kandidat Dosen Pembimbing (maksimal 2)</Form.Label>
                    {loadingDosen ? (
                      <p>Memuat daftar dosen...</p>
                    ) : (
                      <div className="border rounded p-3">
                        {dosenList.length > 0 ? (
                          dosenList.map(dosen => (
                            <Form.Check
                              key={dosen.id}
                              type="checkbox"
                              id={`kandidat-${dosen.id}`}
                              label={`${dosen.nama} ${dosen.nip ? `(${dosen.nip})` : ''}`}
                              checked={values.kandidatPembimbing.includes(dosen.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  if (values.kandidatPembimbing.length < 2) {
                                    setFieldValue('kandidatPembimbing', [...values.kandidatPembimbing, dosen.id]);
                                  }
                                } else {
                                  setFieldValue('kandidatPembimbing', values.kandidatPembimbing.filter(id => id !== dosen.id));
                                }
                              }}
                              disabled={values.kandidatPembimbing.length >= 2 && !values.kandidatPembimbing.includes(dosen.id)}
                              className="mb-2"
                            />
                          ))
                        ) : (
                          <p className="text-muted">Tidak ada dosen tersedia</p>
                        )}
                      </div>
                    )}
                    <Form.Text className="text-muted">
                      Pilih maksimal 2 dosen sebagai kandidat pembimbing. Admin akan menentukan siapa yang menjadi pembimbing Anda.
                    </Form.Text>
                    {touched.kandidatPembimbing && errors.kandidatPembimbing && (
                      <div className="text-danger mt-1">{errors.kandidatPembimbing}</div>
                    )}
                  </Form.Group>
                  
                  <Alert variant="warning">
                    <strong>Penting:</strong> Pastikan Anda telah mengupload file proposal ke folder Google Drive dengan nama file sesuai format <code>prop_NIM_Nama_Anda</code> sebelum mengirimkan formulir ini.
                  </Alert>
                  
                  <Button 
                    variant="primary" 
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Memproses...' : 'Simpan'}
                  </Button>
                </Form>
              );
            }}
          </Formik>
        </Card.Body>
      </Card>
      
      {/* Modal Perubahan Pembimbing */}
      {existingTA && (
        <Modal show={showChangeSupervisorModal} onHide={() => {
          setShowChangeSupervisorModal(false);
          setSelectedNewPembimbing([]);
          setError('');
        }}>
          <Modal.Header closeButton>
            <Modal.Title>Ajukan Perubahan Pembimbing</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            <Alert variant="info">
              <p><strong>Pembimbing Saat Ini:</strong></p>
              <ul>
                {Array.isArray(existingTA.pembimbing) && existingTA.pembimbing.length > 0 ? (
                  existingTA.pembimbing.map((p, idx) => (
                    <li key={p.id}>P{idx+1}: {p.nama} (NIP: {p.nip || '-'})</li>
                  ))
                ) : (
                  <li>Data pembimbing tidak tersedia</li>
                )}
              </ul>
            </Alert>
            
            <Form.Group className="mb-3">
              <Form.Label>Pilih Pembimbing Baru (maksimal 2) <span className="text-danger">*</span></Form.Label>
              {dosenList.map((dosen) => (
                <Form.Check
                  key={dosen.id}
                  type="checkbox"
                  label={`${dosen.nama} (NIP: ${dosen.nip || '-'})`}
                  checked={selectedNewPembimbing.includes(dosen.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      if (selectedNewPembimbing.length < 2) {
                        setSelectedNewPembimbing([...selectedNewPembimbing, dosen.id]);
                      }
                    } else {
                      setSelectedNewPembimbing(selectedNewPembimbing.filter(id => id !== dosen.id));
                    }
                  }}
                  disabled={selectedNewPembimbing.length >= 2 && !selectedNewPembimbing.includes(dosen.id)}
                />
              ))}
              <Form.Text className="text-muted">
                Pilih maksimal 2 dosen sebagai pembimbing baru. Usulan akan ditinjau oleh admin.
              </Form.Text>
            </Form.Group>
            
            <Alert variant="warning">
              <strong>Perhatian:</strong> Setelah pembimbing diganti, sesi bimbingan dengan pembimbing lama tetap tersimpan untuk catatan, tetapi menu bimbingan Anda di dashboard pembimbing lama akan hilang.
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => {
              setShowChangeSupervisorModal(false);
              setSelectedNewPembimbing([]);
              setError('');
            }}>
              Batal
            </Button>
            <Button 
              variant="primary" 
              onClick={async () => {
                if (selectedNewPembimbing.length === 0) {
                  setError("Pilih minimal 1 pembimbing baru");
                  return;
                }
                
                try {
                  setLoading(true);
                  setError('');
                  
                  const taRef = doc(db, "tugasAkhir", existingTA.id);
                  
                  // Simpan usulan perubahan pembimbing
                  const pembimbingLama = Array.isArray(existingTA.pembimbingId) ? existingTA.pembimbingId : 
                                        (existingTA.pembimbingId ? [existingTA.pembimbingId] : []);
                  
                  await updateDoc(taRef, {
                    usulanPerubahanPembimbing: {
                      pembimbingBaruId: selectedNewPembimbing,
                      pembimbingLamaId: pembimbingLama,
                      status: 'pending',
                      tanggal_usulan: new Date().toISOString(),
                      diajukanOleh: {
                        mahasiswa: currentUser.uid,
                        nama: currentUser.displayName || 'Mahasiswa',
                        timestamp: new Date().toISOString()
                      }
                    }
                  });
                  
                  setSuccess(true);
                  setShowChangeSupervisorModal(false);
                  setSelectedNewPembimbing([]);
                  
                  // Refresh data
                  await checkExistingTA();
                  setTimeout(() => {
                    setSuccess(false);
                    navigate('/dashboard');
                  }, 2000);
                } catch (err) {
                  console.error('Error submitting supervisor change request:', err);
                  setError('Gagal menyimpan usulan perubahan pembimbing: ' + err.message);
                } finally {
                  setLoading(false);
                }
              }}
              disabled={loading || selectedNewPembimbing.length === 0}
            >
              {loading ? 'Menyimpan...' : 'Ajukan Perubahan'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Container>
  );
};

export default PendaftaranTA;
